import type {
  IAIReviewIssueCodingResultItem,
  IAIReviewJobResultRelevancyListResponse,
  IAIReviewRelevancyDocResultApiRow,
  IAIReviewRelevancyDocSummary,
  IAIReviewRelevancyDocSummaryResponse,
} from '../types/aiReview.type';

const toRecord = (value: unknown): Record<string, unknown> | null =>
  value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const asArray = (value: unknown): unknown[] | null => {
  if (Array.isArray(value)) return value;
  const record = toRecord(value);
  if (record && Array.isArray(record.$values)) {
    return record.$values;
  }
  return null;
};

const getFirstDocResultArray = (
  payload: Record<string, unknown>
): unknown[] | null =>
  asArray(payload.RelevancyDocResult) ??
  asArray(payload.relevancyDocResult) ??
  asArray(payload.RelevancyDocResults) ??
  asArray(payload.relevancyDocResults) ??
  asArray(payload.documents) ??
  asArray(payload.result) ??
  asArray(payload.Result);

const getRelevancyDocRows = (data: unknown): unknown[] => {
  if (Array.isArray(data)) return data;
  const payload = toRecord(data);
  if (!payload) return [];

  const topLevelRows = getFirstDocResultArray(payload);
  if (topLevelRows) return topLevelRows;

  const nested =
    toRecord(payload.data) ??
    toRecord(payload.Data) ??
    toRecord(payload.result) ??
    toRecord(payload.Result) ??
    toRecord(payload.payload) ??
    toRecord(payload.Payload);
  if (!nested) return [];

  return getFirstDocResultArray(nested) ?? asArray(nested) ?? [];
};

const toFiniteNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const isConfidenceValue = (value: unknown): value is number | string => {
  if (isFiniteNumber(value)) {
    return true;
  }

  if (typeof value !== 'string') {
    return false;
  }

  const normalized = value.trim();
  if (!normalized) {
    return false;
  }

  const numericValue = normalized.endsWith('%')
    ? normalized.slice(0, -1).trim()
    : normalized;

  return numericValue !== '' && Number.isFinite(Number(numericValue));
};

const isRelevancyDocResultApiRow = (
  value: unknown
): value is IAIReviewRelevancyDocResultApiRow => {
  if (!isRecord(value)) {
    return false;
  }

  return (
    isFiniteNumber(value.documentId) &&
    isFiniteNumber(value.reviewJobSubmittedId) &&
    (typeof value.docId === 'string' || isFiniteNumber(value.docId)) &&
    typeof value.relevency === 'string' &&
    typeof value.groundTruth === 'string' &&
    typeof value.confidence === 'string' &&
    typeof value.reason === 'string' &&
    typeof value.isFlaggedForHumanReview === 'boolean'
  );
};

export const normalizeRelevancyDocResultResponse = (
  data: unknown
): IAIReviewJobResultRelevancyListResponse => {
  const payload = toRecord(data);
  const nested =
    toRecord(payload?.data) ??
    toRecord(payload?.Data) ??
    toRecord(payload?.result) ??
    toRecord(payload?.Result);
  const rows = getRelevancyDocRows(data);

  const documents = rows.map((row, index) => {
    const item: Record<string, unknown> = isRelevancyDocResultApiRow(row)
      ? (row as unknown as Record<string, unknown>)
      : (toRecord(row) ?? {});
    const documentId = toFiniteNumber(
      item.documentId ?? item.DocumentId ?? item.documentID
    );
    const reviewJobSubmittedId = toFiniteNumber(
      item.reviewJobSubmittedId ?? item.ReviewJobSubmittedId
    );
    const docIdValue = item.docId ?? item.DocId;
    const relevancyValue =
      item.relevancy ?? item.Relevancy ?? item.relevency ?? item.Relevency;

    return {
      documentId: documentId || index + 1,
      reviewJobSubmittedId,
      docId: docIdValue == null ? '' : String(docIdValue),
      relevancy: String(relevancyValue ?? ''),
      groundTruth: String(item.groundTruth ?? item.GroundTruth ?? ''),
      confidence: String(item.confidence ?? item.Confidence ?? ''),
      reason: String(item.reason ?? item.Reason ?? ''),
      isFlaggedForHumanReview: Boolean(
        item.isFlaggedForHumanReview ?? item.IsFlaggedForHumanReview
      ),
    };
  });

  const rawTotal =
    payload?.totalDocCount ??
    payload?.TotalDocCount ??
    payload?.totalDocs ??
    nested?.totalDocCount ??
    nested?.TotalDocCount ??
    nested?.totalDocs;
  const parsedTotal = Number(rawTotal);
  const totalDocs = Array.isArray(data)
    ? documents.length
    : Number.isFinite(parsedTotal) && parsedTotal > 0
      ? parsedTotal
      : documents.length;

  return {documents, totalDocs};
};

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

const isNullableString = (value: unknown): value is string | null | undefined =>
  value === null || value === undefined || typeof value === 'string';

const isNullableStringArray = (
  value: unknown
): value is string[] | null | undefined =>
  value === null || value === undefined || isStringArray(value);

const isRelevancyResponsiveness = (
  value: unknown
): value is IAIReviewRelevancyDocSummaryResponse['responsiveness'] =>
  value === 'Responsive' || value === 'Non-Responsive';

const isNullableRelevancyResponsiveness = (value: unknown) =>
  value === null ||
  value === undefined ||
  value === '' ||
  isRelevancyResponsiveness(value);

const isSummaryConfidenceValue = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim() === '') ||
  isConfidenceValue(value);

const isIssueCodingResponsive = (
  value: unknown
): value is IAIReviewIssueCodingResultItem['responsive'] =>
  value === 'Yes' || value === 'No';

const isIssueCodingResultItem = (
  value: unknown
): value is IAIReviewIssueCodingResultItem => {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.issueCode === 'string' &&
    isIssueCodingResponsive(value.responsive) &&
    isConfidenceValue(value.confidence) &&
    typeof value.reason === 'string'
  );
};

const normalizeIssueCodingResult = (
  value: unknown
): IAIReviewIssueCodingResultItem[] | undefined => {
  if (value == null) {
    return undefined;
  }
  if (!Array.isArray(value)) {
    return undefined;
  }

  const rows = value.filter(isIssueCodingResultItem);
  return rows.length > 0 ? rows : [];
};

const isRelevancyDocSummaryResponse = (
  data: unknown
): data is IAIReviewRelevancyDocSummaryResponse => {
  if (!isRecord(data)) {
    return false;
  }

  return (
    isNullableRelevancyResponsiveness(data.responsiveness) &&
    isSummaryConfidenceValue(data.confidence) &&
    isNullableString(data.reason) &&
    isNullableStringArray(data.tags) &&
    typeof data.isExcluded === 'boolean' &&
    isNullableString(data.excludedDetails) &&
    typeof data.isFlaggedForHumanReview === 'boolean' &&
    isNullableString(data.humanReviewDetails) &&
    isNullableStringArray(data.humanReviewTags)
  );
};

export const normalizeRelevancyDocSummaryResponse = (
  data: unknown
): IAIReviewRelevancyDocSummary => {
  if (data === null || data === undefined) {
    return {
      responsiveness: '',
      confidence: '',
      reason: '',
      tags: [],
      isExcluded: false,
      excludedDetails: '',
      isFlaggedForHumanReview: false,
      humanReviewDetails: '',
      humanReviewTags: [],
    };
  }

  if (!isRelevancyDocSummaryResponse(data)) {
    throw new Error('Invalid Relevancy document summary response');
  }

  const issueCodingResult = normalizeIssueCodingResult(
    (data as Record<string, unknown>).issueCodingResult
  );

  return {
    responsiveness: isRelevancyResponsiveness(data.responsiveness)
      ? data.responsiveness
      : '',
    confidence:
      typeof data.confidence === 'number' || typeof data.confidence === 'string'
        ? data.confidence
        : '',
    reason: typeof data.reason === 'string' ? data.reason : '',
    tags: isStringArray(data.tags) ? data.tags : [],
    isExcluded: data.isExcluded,
    excludedDetails:
      typeof data.excludedDetails === 'string' ? data.excludedDetails : '',
    isFlaggedForHumanReview: data.isFlaggedForHumanReview,
    humanReviewDetails:
      typeof data.humanReviewDetails === 'string'
        ? data.humanReviewDetails
        : '',
    humanReviewTags: isStringArray(data.humanReviewTags)
      ? data.humanReviewTags
      : [],
    ...(issueCodingResult ? {issueCodingResult} : {}),
  };
};
